/**
 * Single-line user fields: what they accept, and how a value is cleaned
 * before it is stored.
 *
 * Sharetribe Console has a "Single-line text" field type (a text field on
 * the forms, one line on the profile) next to "Long text" (a text area),
 * but the web template renders every text field as a text area and cannot
 * tell them apart. The business fields are defined in code, so they say it
 * themselves: `saveConfig.singleLine`, with the input's own attributes
 * (`maxLength`, `autoComplete`, `inputMode`) and, where the value has a
 * format, `saveConfig.validateAs`: 'email', 'postcode', 'orgNumber', 'url'.
 */

export const SINGLE_LINE_FORMATS = ['email', 'postcode', 'orgNumber', 'url'];

/** Does this field take one line of text? */
export const isSingleLineField = fieldConfig =>
  fieldConfig?.saveConfig?.singleLine === true ||
  SINGLE_LINE_FORMATS.includes(fieldConfig?.saveConfig?.validateAs);

const ORG_NUMBER_WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2];

/** "123 456 789" → "123456789" */
export const orgNumberDigits = value => `${value || ''}`.replace(/\s+/g, '');

/**
 * A Norwegian organisation number: nine digits whose last is the modulus-11
 * check digit of the first eight (weights 3 2 7 6 5 4 3 2), which catches
 * most typing mistakes. Spaces are allowed and ignored.
 *
 * @param {string} value
 * @returns {'ok'|'length'|'checkDigit'}
 */
export const checkOrgNumber = value => {
  const digits = orgNumberDigits(value);
  if (!/^\d{9}$/.test(digits)) {
    return 'length';
  }
  const sum = ORG_NUMBER_WEIGHTS.reduce((acc, weight, i) => acc + weight * Number(digits[i]), 0);
  const remainder = sum % 11;
  const check = remainder === 0 ? 0 : 11 - remainder;
  return check !== 10 && check === Number(digits[8]) ? 'ok' : 'checkDigit';
};

/**
 * A website address as people type it: with or without https://, no spaces,
 * a dot in the host.
 */
export const isWebAddress = value => {
  const text = `${value || ''}`.trim();
  // No spaces, and no @: an e-mail address is not a website, and `@` would
  // otherwise be read as a login name in front of the host.
  if (!text || /\s/.test(text) || text.includes('@')) {
    return false;
  }
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
    return ['http:', 'https:'].includes(url.protocol) && url.hostname.includes('.');
  } catch (e) {
    return false;
  }
};

/**
 * The value to store for a single-line field: no line breaks, trimmed, and
 * normalised by format (an org number without spaces, a website with
 * https://). An empty value is stored as null. Other fields are untouched.
 *
 * @param {Object} fieldConfig
 * @param {*} value
 * @returns {*}
 */
export const normalizeUserFieldValue = (fieldConfig, value) => {
  if (!isSingleLineField(fieldConfig) || typeof value !== 'string') {
    return value;
  }
  const oneLine = value
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!oneLine) {
    return null;
  }
  const format = fieldConfig.saveConfig?.validateAs;
  if (format === 'orgNumber') {
    return orgNumberDigits(oneLine);
  }
  if (format === 'url') {
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(oneLine) ? oneLine : `https://${oneLine}`;
  }
  return oneLine;
};
